import React from "react";
import { ArrowRight, Check, Clock, Zap, Sparkles } from "lucide-react";

const HERO_BULLETS = [
  "Modern, mobile-friendly design",
  "See the website concept before you pay",
  "Reasonable revisions before final approval",
  "Hosting, support, and updates handled for you",
];

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-24 md:pb-32 bg-[#0B0F19] text-white overflow-hidden"
    >
      {/* Background grid + glow */}
      <div className="absolute inset-0 hero-grid pointer-events-none" />
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-blue-600/20 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-indigo-600/20 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left */}
        <div className="lg:col-span-7 fade-up">
          <div
            data-testid="hero-offer-badge"
            className="inline-flex items-center gap-2 rounded-full bg-yellow-400/10 border border-yellow-400/30 px-4 py-1.5 text-xs sm:text-sm font-black tracking-widest uppercase text-yellow-300"
          >
            <Sparkles size={14} className="text-yellow-300" />
            Limited-Time Website Revival Special: 50% OFF
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mt-5 leading-[1.02]"
          >
            Your Website Might Be
            <span className="block text-gradient-blue">Costing You Customers</span>
            <span className="block">Every Day</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed"
          >
            We help business owners turn outdated, missing, or unprofessional websites into modern
            websites that build trust, generate calls, and can be ready to launch the same day
            after approval.
          </p>

          {/* Pricing block above the fold */}
          <div
            data-testid="hero-price-block"
            className="mt-8 rounded-2xl glass-dark px-5 sm:px-6 py-5 max-w-xl"
          >
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-x-3 gap-y-1">
              <span className="text-xs sm:text-sm uppercase tracking-widest text-slate-300 font-bold whitespace-nowrap">
                Website Build
              </span>
              <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                <span data-testid="hero-price-original" className="text-lg sm:text-xl font-bold text-slate-500 line-through">
                  $1,499
                </span>
                <span data-testid="hero-price-special" className="font-display text-4xl sm:text-5xl font-black text-gradient-gold leading-none">
                  $750
                </span>
                <span className="text-xs sm:text-sm text-yellow-300 font-black uppercase tracking-wider">
                  Today
                </span>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-baseline gap-x-3 gap-y-1 border-t border-white/5 pt-4">
              <span className="text-xs sm:text-sm uppercase tracking-widest text-slate-300 font-bold whitespace-nowrap">
                Care &amp; Hosting
              </span>
              <div className="flex items-baseline gap-x-2">
                <span className="font-display text-3xl sm:text-4xl font-black text-white leading-none">$99</span>
                <span className="text-sm text-slate-400">/month</span>
              </div>
            </div>
            <div
              data-testid="hero-launch-note"
              className="mt-4 flex items-start gap-2 text-xs sm:text-sm text-slate-300 border-t border-white/5 pt-4"
            >
              <Clock size={14} className="text-blue-400 mt-0.5 shrink-0" />
              <span>
                Same-day launch available after approval when all required business information
                and domain access are ready.
              </span>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#contact"
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold px-7 py-4 transition-all hover:-translate-y-0.5 glow-blue"
            >
              Request My Website Review <ArrowRight size={18} />
            </a>
            <a
              href="#pricing"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-bold px-7 py-4 transition-all backdrop-blur-md"
            >
              See What’s Included
            </a>
          </div>

          {/* Hero bullets — tightened */}
          <ul
            data-testid="hero-bullets"
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-2xl"
          >
            {HERO_BULLETS.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                <Check size={16} className="mt-0.5 text-emerald-400 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right - hero visual (outdated → modern transformation) */}
        <div className="lg:col-span-5 relative h-auto lg:h-[560px] flex items-center justify-center fade-up">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="absolute h-[420px] w-[420px] rounded-full bg-blue-500/15 blur-[100px]" />
          </div>
          <img
            src="/home-1.png"
            alt="Instant Site Revival — an outdated, cluttered website transformed into a modern, mobile-friendly, high-converting local business website in days, not months"
            loading="eager"
            className="relative z-10 w-full h-auto max-h-[560px] object-contain rounded-3xl shadow-[0_30px_90px_-20px_rgba(59,130,246,0.35)] ring-1 ring-white/10"
          />
          <div className="absolute -bottom-2 right-4 inline-flex items-center gap-2 rounded-full glass-dark px-4 py-2 text-xs font-bold text-yellow-300 z-20">
            <Zap size={14} /> Same-Day Launch Available
          </div>
        </div>
      </div>
    </section>
  );
}
